import ButtonRaw from '@/components/Buttons/ButtonRaw';
import Image from '@/components/Image/Image';
import RemoteIcon from '@/components/RemoteIcon/RemoteIcon';
import {getConfig} from '@/config/config';
import useExternalLinks from '@/hooks/useExternalLinks';
import {formatDate} from '@/services/dateFormat';
import type {ExternalLinkExtendedModel, IconModel} from '@/types/aem';
import type {UiReward} from '@/types/loyalty';
import {formatBadgeImageUrl} from '@/utils/journeysUtils';
import {formatString} from '@/utils/stringUtils';
import {twx} from '@/utils/tailwindUtils';

type RewardCardProps = {
    reward: UiReward;
    redeemedLabel?: string | null;
    notEarnedRewardIcon?: IconModel | null;
    isEarned?: boolean;
    showImage?: boolean;
    className?: string;
    rewardDetailExternalLink?: ExternalLinkExtendedModel | null;
};

const RewardCard = ({
    reward,
    redeemedLabel,
    notEarnedRewardIcon,
    isEarned = true,
    showImage = true,
    className,
    rewardDetailExternalLink,
}: RewardCardProps) => {
    const {title, description, imageUrl, redeemedDate, id: rewardId, journeyId} = reward;
    const src = formatBadgeImageUrl(imageUrl, 256, 256);
    const formattedDate = getFormattedRedeemedDate(redeemedDate);
    const redeemedDateLabel = formattedDate && formatString(redeemedLabel, formattedDate);
    const redirect = useExternalLinks({externalLink: rewardDetailExternalLink, params: {journeyId, rewardId}});

    return (
        <ButtonRaw
            data-testid='reward-card'
            onClick={() => redirect?.()}
            disabled={!redirect}
            className={twx(
                'flex w-full cursor-pointer items-stretch gap-4 overflow-hidden border-0 bg-secondary p-0 text-left text-romance first:rounded-t-xl last:rounded-b-xl disabled:cursor-default',
                className
            )}
        >
            <div className='relative shrink-0 overflow-hidden'>
                {showImage && src && (
                    <Image src={src} alt='' aria-hidden className='h-full min-h-31 w-22 object-cover' />
                )}
                {!isEarned && <NotEarnedOverlay icon={notEarnedRewardIcon} />}
            </div>

            <div className='flex min-w-0 flex-1 flex-col justify-center gap-1 py-4 pr-4'>
                {title && <span className='m-0 line-clamp-2 font-bold text-16 leading-6'>{title}</span>}
                {description && <p className='m-0 line-clamp-2 text-14 leading-5 tracking-04'>{description}</p>}
                {redeemedDateLabel && (
                    <span className='mt-1 text-12 text-primary-dark-85 leading-4 tracking-04'>{redeemedDateLabel}</span>
                )}
            </div>
        </ButtonRaw>
    );
};

type NotEarnedOverlayProps = {
    icon?: IconModel | null;
};

const NotEarnedOverlay = ({icon}: NotEarnedOverlayProps) => (
    <div className='absolute inset-0 flex items-center justify-center bg-secondary/70'>
        <RemoteIcon icon={icon} className='size-10' />
    </div>
);

const getFormattedRedeemedDate = (redeemedDate?: string | null): string | null => {
    if (!redeemedDate) return null;

    const {dateFormat} = getConfig();
    if (!dateFormat) return null;

    try {
        return formatDate(redeemedDate, dateFormat);
    } catch {
        return null;
    }
};

export default RewardCard;
